import React from 'react';
import './Documentation.css';

function Documentation() {
  return (
    <div className="main-content">
      <h1>📖 App Documentation</h1>
      <p>
        Welcome! This guide explains how to use the <strong>Package & Deploy</strong> app to migrate your Tableau Next dashboards between Salesforce orgs.
      </p>
      <hr className="divider" />

      <h2>🎥 Video Tutorial (4 Minutes)</h2>
      <p>Watch this short video for a complete walkthrough of the packaging and deployment process.</p>
      <div className="video-container">
        <iframe
          className="video-iframe"
          title="Tableau Next - Package and Deploy web app"
          src="https://play.vidyard.com/LfnwvkZUbgo14BxMhd22a5.html?"
          width="1280"
          height="720"
          scrolling="no"
          frameBorder="0"
          allowTransparency="true"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>

      <hr className="divider" />

      <h2>Core Concepts</h2>
      <h3>What is a Package?</h3>
      <p>
        A package is a single <code>.json</code> file that contains everything needed to recreate a dashboard in another Salesforce org. It's structured with two main sections:
      </p>
      <ul>
        <li>
          <strong><code>components</code></strong>: This is the main blueprint. It contains the processed, ready-to-deploy definitions for the dashboard, all its visualizations, and the structure of its semantic model. All org-specific IDs and names have been replaced with generic placeholders.
        </li>
        <li>
          <strong><code>requirements</code></strong>: This is a list of all the specific dependencies the dashboard needs to function correctly. It includes every metric, parameter, and field (dimensions, measurements) the dashboard uses. This section is crucial for the validation step during deployment.
        </li>
      </ul>

      <hr className="divider" />

      <h2>📦 How to Package a Dashboard</h2>
      <p>Follow these steps in the <strong>source org</strong> (the org where your dashboard currently exists).</p>
      <ol>
        <li><strong>Navigate to the App</strong>: From the sidebar, select the <strong>🚀 Package & Deploy</strong> page.</li>
        <li><strong>Select the 'Package' Tab</strong>: Ensure you are on the <code>Package Dashboard</code> tab.</li>
        <li><strong>Fetch Dashboards</strong>: Click the <strong>Fetch Available Dashboards</strong> button.</li>
        <li><strong>Choose a Dashboard</strong>: Select the dashboard you want to package from the dropdown menu.</li>
        <li><strong>Generate the Package</strong>: Click the <strong>Generate Package</strong> button. The app will analyze all dependencies and create the single package file in memory.</li>
        <li><strong>Download</strong>: Once processing is complete, click the <strong>Download Package File (.json)</strong> button to save the file to your computer.</li>
      </ol>

      <hr className="divider" />

      <h2>🚀 How to Deploy a Package</h2>
      <div className="alert-warning">
        <strong>Important:</strong> Before starting, log out and log back into the <strong>target org</strong> (the org where you want to deploy the dashboard).
      </div>
      <div className="alert-error">
        <strong>Prerequisite: Deploy Your Data Cloud Objects First!</strong>
        <br /><br />
        This application <strong>DOES NOT</strong> migrate or create the underlying Data Cloud infrastructure (Data Streams, DLOs, DMOs). Before deploying, you <strong>MUST</strong> ensure the required Data Model Objects already exist in the target org, typically by deploying a <strong>Data Kit</strong> first.
        <br /><br />
        <a 
          href="https://help.salesforce.com/s/articleView?id=sf.c360_a_data_package_kits.htm&language=en_US&type=5"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about creating and using Data Kits here.
        </a>
      </div>

      <h3>Step 1: Upload Your Package File</h3>
      <p>
        On the <code>Deploy Dashboard</code> tab, upload the <code>_package.json</code> file you downloaded. Upon a successful upload, a confirmation message will appear showing the name of the dashboard in the package.
      </p>

      <h3>Step 2: Configure Workspace & Semantic Model</h3>
      <p>You have two key choices to make:</p>
      <div className="two-column-section">
        <div className="info-box">
          <h4>Workspace Options</h4>
          <ul>
            <li><strong>Create a new workspace</strong>: The app will create a brand new workspace for your dashboard.</li>
            <li><strong>Use an existing workspace</strong>: The app will deploy the dashboard into a workspace you select from a list.</li>
          </ul>
        </div>
        <div className="info-box">
          <h4>Semantic Model Options</h4>
          <ul>
            <li><strong>Create a new semantic model</strong>: The app will create a new semantic model exactly as defined in the package.</li>
            <li><strong>Use an existing semantic model</strong>: The app will attempt to use an existing model in the target org. <strong>This will trigger a validation step.</strong></li>
          </ul>
        </div>
      </div>

      <h3>Step 3: Dependency Mapping (If Required)</h3>
      <p>
        This step only appears if you choose to <strong>use an existing semantic model</strong>. The app validates that the model you selected has all the fields listed in the package's <code>requirements</code> section. If dependencies are missing, you must map them to available fields in the target model.
      </p>
      <div className="alert-info">
        <strong>Mapping Feature (BETA)</strong>
        <br /><br />
        Please be aware of the following rules when mapping fields:
        <ul>
          <li>You can only map fields of the same type (e.g., Calculated Dimension to Calculated Dimension, DMO Field to DMO Field).</li>
          <li>The data types must also match (e.g., Text to Text, Number to Number).</li>
        </ul>
      </div>

      <h3>Step 4: Deploy</h3>
      <p>
        Once all configurations are set, click the <strong>Deploy Dashboard</strong> button. A progress bar will appear, showing the real-time status of each step in the deployment process.
      </p>

      <h3>Step 5: Finish</h3>
      <p>After a successful deployment, a confirmation message will appear along with two options:</p>
      <ul>
        <li><strong>Start New Deployment</strong>: Clears the form and allows you to begin another deployment.</li>
        <li><strong>Go to Workspace</strong>: A direct link to the newly created or updated workspace in your Salesforce org.</li>
      </ul>

      <hr className="divider" />

      <h2>⚠️ Known Limitations</h2>
      <div className="alert-warning">
        This tool is designed for a specific, common migration scenario. Please be aware of the following limitations in the current version:
      </div>
      <ul>
        <li><strong>Single Dashboard Only</strong>: The application can only package and deploy one dashboard at a time.</li>
        <li><strong>Single Semantic Model</strong>: The dashboard being packaged must rely on a single semantic model. Dashboards that use data from multiple semantic models are not currently supported.</li>
        <li><strong>No Semantic Model Extensions</strong>: The tool does not support semantic models that are extended from other models. The source semantic model must be self-contained.</li>
      </ul>

      <hr className="divider" />

      <h2>F.A.Q. & Troubleshooting</h2>
      <h3>Why is the 'Deploy Dashboard' button disabled?</h3>
      <p>The deploy button will only activate when all conditions are met:</p>
      <ol>
        <li>A <code>_package.json</code> file has been successfully uploaded.</li>
        <li>You have fully configured both the Workspace and Semantic Model options.</li>
        <li>If using an existing semantic model, all missing dependencies must be mapped.</li>
      </ol>

      <h3>I received an error during deployment. What should I do?</h3>
      <p>Read the error message carefully. Common causes include:</p>
      <ul>
        <li><strong>Permissions</strong>: You may not have the necessary Salesforce permissions to create/edit Tableau assets in the target org.</li>
        <li><strong>Data Kit Not Deployed</strong>: The most common error is forgetting to deploy the Data Kit with the required DMOs to the target org first.</li>
        <li><strong>Invalid Mapping</strong>: A field you mapped may not be a compatible type with the required field.</li>
      </ul>
      <p>If you can't resolve the issue, please contact support and provide the error message and your Org ID.</p>
    </div>
  );
}

export default Documentation;

